import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import { carregarVendasDoProduto } from '../../produtos/vendasDoProduto'
import { visaoDoCliente, rotuloDaJanela, abreviarValor } from '../../produtos/historicoDoCliente'
import type { Janela, BarraDoCliente, Totais } from '../../produtos/historicoDoCliente'
import { rotuloDoMes } from '../../produtos/periodosDoHistorico'
import { dataBRDoTexto } from '../../lib/periodo'
import { formatarMoeda } from '../../lib/formatos'
import { ROTULO_ESTAGIO, CLASSE_ESTAGIO } from '../../pedidos/estagio'
import { IconeCaixa, IconeDocumento, IconeMoedas, IconePredio } from './IconesDaFicha'

// Tela do cliente (design aprovado por ela, 24/09 noite, sobre o print que
// ela mandou): o historico de UM cliente com UM produto. Aberta tocando no
// cliente da lista do Historico da ficha. O Voltar do cabecalho do app leva de
// volta a ficha, que reabre no Historico (?aba=historico).

const JANELAS: Janela[] = [12, 6, 3]

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const decimal = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

type Medida = 'faturamento' | 'pedidos' | 'unidades'
const GRAFICOS: Array<{ medida: Medida; titulo: string; sub: string; cor: string; fundo: string; icone: ReactNode; valor: (n: number) => string }> = [
  { medida: 'faturamento', titulo: 'Faturamento', sub: 'valor em R$', cor: 'bg-emerald-600', fundo: 'bg-emerald-50', icone: <IconeMoedas tamanho={16} />, valor: abreviarValor },
  { medida: 'pedidos', titulo: 'Pedidos', sub: 'quantidade de pedidos', cor: 'bg-indigo-500', fundo: 'bg-indigo-50', icone: <IconeDocumento tamanho={16} />, valor: (n) => (n === 0 ? '' : String(n)) },
  { medida: 'unidades', titulo: 'Unidades', sub: 'quantidade de unidades', cor: 'bg-cyan-600', fundo: 'bg-cyan-50', icone: <IconeCaixa tamanho={16} />, valor: (n) => (n === 0 ? '' : String(n)) },
]

function texto(medida: Medida, t: Totais): string {
  if (medida === 'faturamento') return reais(t.faturamento)
  return String(t[medida])
}

export default function HistoricoDoCliente() {
  const { id: produtoId, clienteId } = useParams()
  const navegar = useNavigate()
  const [janela, setJanela] = useState<Janela>(12)
  const [mesTocado, setMesTocado] = useState<string | null>(null)
  const [verTodas, setVerTodas] = useState(false)

  const produto = useLiveQuery(async () => (await buscarVivo(banco.produtos, produtoId)) ?? null, [produtoId])
  const cliente = useLiveQuery(async () => (await buscarVivo(banco.clientes, clienteId)) ?? null, [clienteId])
  const vendas = useLiveQuery(
    async () => (produtoId ? (await carregarVendasDoProduto(produtoId)).filter((v) => v.clienteId === clienteId) : []),
    [produtoId, clienteId],
  )
  if (produto === undefined || cliente === undefined || vendas === undefined) {
    return <p className="p-4 text-sm text-slate-500">Carregando…</p>
  }
  if (produto === null || cliente === null) {
    return (
      <section className="p-4 space-y-3">
        <p className="text-sm text-slate-700">Não encontramos este cliente ou este produto.</p>
        <button type="button" onClick={() => navegar(-1)} className="text-sm underline text-slate-600">Voltar</button>
      </section>
    )
  }

  const visao = visaoDoCliente(vendas, new Date(), janela, mesTocado)
  const lista = verTodas ? visao.lista : visao.lista.slice(0, 5)
  const tocar = (chave: string) => { setMesTocado(visao.mesTocado === chave ? null : chave); setVerTodas(false) }

  return (
    <section className="p-4 space-y-3">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Histórico de vendas</h2>
        <div className="flex gap-3 items-center mt-2">
          <span className="w-10 h-10 shrink-0 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center"><IconePredio tamanho={20} /></span>
          <span className="min-w-0">
            <span className="block font-bold text-slate-900 truncate">{cliente.nome_fantasia || cliente.razao_social}</span>
            <span className="block text-xs text-slate-600 truncate">{produto.descricao}</span>
          </span>
        </div>
      </div>

      <div className="flex bg-slate-200 rounded-full p-1" role="group" aria-label="Período">
        {JANELAS.map((n) => (
          <button
            key={n} type="button" aria-pressed={janela === n}
            onClick={() => { setJanela(n); setMesTocado(null); setVerTodas(false) }}
            className={`flex-1 rounded-full py-1.5 text-sm ${janela === n ? 'bg-[#0B4A47] text-white font-semibold' : 'text-slate-700'}`}
          >{n} meses</button>
        ))}
      </div>

      {visao.mesTocado !== null && (
        <p className="text-xs text-slate-600">Números de <strong>{rotuloDoMes(visao.mesTocado)}</strong></p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {GRAFICOS.map((g) => (
          <div key={g.medida} className={`${g.fundo} rounded-xl border border-slate-200 p-2 min-w-0`}>
            <p className="text-[11px] text-slate-600">{g.titulo}</p>
            <p className="text-base font-extrabold text-slate-900 truncate">{texto(g.medida, visao.resumo)}</p>
            {visao.anterior !== null && <p className="text-[10px] text-slate-500 truncate">antes: {texto(g.medida, visao.anterior)}</p>}
          </div>
        ))}
      </div>

      {GRAFICOS.map((g) => (
        <Grafico key={g.medida} {...g} barras={visao.barras} mesTocado={visao.mesTocado} periodo={rotuloDaJanela(visao.meses)} aoTocar={tocar} />
      ))}

      <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-2 items-center bg-emerald-50 rounded-xl p-3 text-[11px] text-slate-600">
        <span className="font-bold text-slate-800 text-xs">Média mensal</span>
        <span><b className="block text-sm text-slate-900">{reais(visao.media.faturamento)}</b>faturamento</span>
        <span><b className="block text-sm text-slate-900">{decimal(visao.media.pedidos)}</b>pedidos</span>
        <span><b className="block text-sm text-slate-900">{decimal(visao.media.unidades)}</b>unidades</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <div className="flex justify-between items-center mb-1 gap-2">
          <p className="text-sm font-semibold text-slate-800">
            {visao.mesTocado === null ? 'Últimos pedidos' : `Pedidos de ${rotuloDoMes(visao.mesTocado)}`}
          </p>
          {visao.mesTocado !== null && (
            <button type="button" onClick={() => { setMesTocado(null); setVerTodas(false) }} className="text-xs text-slate-500 underline">
              Voltar aos {janela} meses
            </button>
          )}
          {visao.lista.length > 5 && (
            <button type="button" onClick={() => setVerTodas((v) => !v)} className="text-xs border border-slate-300 rounded-full px-3 py-1">
              {verTodas ? 'Ver menos' : 'Ver todas ›'}
            </button>
          )}
        </div>
        {lista.length === 0 ? (
          <p className="text-sm text-slate-500">{visao.mesTocado === null ? 'Nenhuma compra neste período.' : 'Nenhuma compra neste mês.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lista.map((v, i) => (
              <li key={`${v.pedidoId}-${i}`}>
                <button type="button" onClick={() => navegar(`/pedidos/${v.pedidoId}`)} className="w-full text-left py-2 flex gap-2 items-center">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-800">Pedido {v.numero}</span>
                    <span className="block text-xs text-slate-500">{dataBRDoTexto(v.data)}</span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-sm text-slate-800">{v.unidades} un. · {formatarMoeda(v.valorTotal)}</span>
                    <span className={`inline-block text-[11px] rounded-full px-2 ${CLASSE_ESTAGIO[v.status]}`}>{ROTULO_ESTAGIO[v.status]}</span>
                  </span>
                  <span aria-hidden="true" className="text-slate-400">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

// Um dos 3 graficos. Os 3 sao clicaveis e tocar num mes escolhe o mesmo mes
// nos 3 (decisao dela). Com um mes tocado, as outras barras ficam "quase marca
// d'agua" (pedido dela) e so a do mes fica com a cor forte.
function Grafico(props: {
  medida: Medida; titulo: string; sub: string; cor: string; icone: ReactNode; valor: (n: number) => string
  barras: BarraDoCliente[]; mesTocado: string | null; periodo: string; aoTocar: (chave: string) => void
}) {
  const maior = Math.max(1, ...props.barras.map((b) => b[props.medida]))
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
      <div className="flex gap-2 items-center mb-2">
        <span className={`w-7 h-7 rounded-full ${props.cor} text-white grid place-items-center`}>{props.icone}</span>
        <span>
          <span className="block text-sm font-bold text-slate-900">{props.titulo}</span>
          <span className="block text-[11px] text-slate-500">{props.sub} · {props.periodo}</span>
        </span>
      </div>
      <div className="flex items-end gap-[3px] h-28">
        {props.barras.map((b) => {
          const n = b[props.medida]
          const escolhido = props.mesTocado === b.chave
          const apagado = props.mesTocado !== null && !escolhido
          return (
            <button
              key={b.chave} type="button" onClick={() => props.aoTocar(b.chave)}
              aria-label={`${props.titulo} de ${rotuloDoMes(b.chave)}`} aria-pressed={escolhido}
              className="flex-1 min-w-0 h-full flex flex-col items-center justify-end"
            >
              {/* So a barra e o numero ficam "marca d'agua": o nome do mes continua legivel. */}
              <span className={`text-[9px] font-semibold text-slate-800 whitespace-nowrap mb-0.5 ${apagado ? 'opacity-20' : ''}`}>{props.valor(n)}</span>
              <span
                data-barra className={`w-full rounded-t ${n === 0 ? 'bg-slate-300' : props.cor} ${escolhido ? 'ring-2 ring-slate-900 ring-offset-1' : ''} ${apagado ? 'opacity-20' : ''}`}
                style={{ height: n === 0 ? '2px' : `${(n / maior) * 80}%` }}
              />
              <span className={`mt-1 text-[9px] ${escolhido ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{b.rotulo}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
